import { Controller, useFormContext } from 'react-hook-form';
import { useRef, useState, useEffect, useCallback } from 'react';

import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import TextField, { TextFieldProps } from '@mui/material/TextField';

import { formatJobTag, findJobTagRanges, keepTaggedJobsInText } from 'src/utils/job-tags';

// ----------------------------------------------------------------------

interface MentionUser {
  id: string;
  first_name: string;
  last_name: string;
}

export interface MentionJob {
  id: string;
  title: string;
  /** Shown under the title in the suggestions, e.g. the client company. */
  subtitle?: string;
}

type Props = TextFieldProps & {
  name: string;
  users?: MentionUser[];
  onMentionChange?: (mentionedUsers: MentionUser[]) => void;
  /**
   * `#` job tagging (JT1-1738). On only when this is passed: it is called with the text typed after
   * an open `#` so the caller can search for jobs, and with null once the `#` closes.
   */
  onJobQueryChange?: (query: string | null) => void;
  /** The jobs matching the open `#` query. */
  jobs?: MentionJob[];
  jobsLoading?: boolean;
  /** The jobs tagged so far. Controlled: jobs are searched, not all loaded, so they cannot be re-derived from the text. */
  taggedJobs?: MentionJob[];
  onTaggedJobsChange?: (jobs: MentionJob[]) => void;
};

// Job titles have spaces, so a `#` query can too; past this it is text, not a search.
const MAX_JOB_QUERY_LENGTH = 60;

// Shared empty defaults, so an unset prop is the same array on every render.
const NO_JOBS: MentionJob[] = [];

type Trigger = '@' | '#';

export default function RHFMentionTextField({
  name,
  helperText,
  users = [],
  onMentionChange,
  onJobQueryChange,
  jobs = NO_JOBS,
  jobsLoading,
  taggedJobs = NO_JOBS,
  onTaggedJobsChange,
  ...other
}: Props) {

  const { control } = useFormContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [suggestions, setSuggestions] = useState<MentionUser[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [trigger, setTrigger] = useState<Trigger | null>(null);
  // The `#` the user closed with Escape: it stays closed while they keep typing after it.
  const [dismissedJobStart, setDismissedJobStart] = useState<number | null>(null);
  const [, setTaggedUsers] = useState<MentionUser[]>([]);

  const textFieldRef = useRef<HTMLTextAreaElement | null>(null);

  const jobTagging = Boolean(onJobQueryChange);

  // A new result set starts the highlight at the top again.
  useEffect(() => {
    if (trigger === '#') setSelectedIndex(0);
  }, [jobs, trigger]);

  // extract mentioned users from text by searching for each user's full name after @
  const extractAndUpdateMentionedUsers = useCallback((text: string) => {
    const mentioned: MentionUser[] = [];
    const seenIds = new Set<string>();

    // For each user, check if their full name appears after @ in the text
    users.forEach(user => {
      const fullName = `${user.first_name} ${user.last_name}`;
      const searchPattern = `@${fullName}`;

      // Check if the user's name appears in the text (case-insensitive)
      if (text.toLowerCase().includes(searchPattern.toLowerCase())) {
        // Only add unique users
        if (!seenIds.has(user.id)) {
          seenIds.add(user.id);
          mentioned.push(user);
        }
      }
    });

    setTaggedUsers(mentioned);

    if (onMentionChange) {
      onMentionChange(mentioned);
    }
  }, [users, onMentionChange]);

  // Drop the tag of any job whose `#Title` has been edited out of the text.
  const pruneTaggedJobs = useCallback((text: string, current: MentionJob[]) => {
    if (!onTaggedJobsChange || !current.length) return;

    const kept = keepTaggedJobsInText(text, current);

    if (kept.length !== current.length) {
      onTaggedJobsChange(kept);
    }
  }, [onTaggedJobsChange]);

  const closeSuggestions = useCallback(() => {
    setAnchorEl(null);
    setMentionStart(null);
    if (trigger === '#') onJobQueryChange?.(null);
    setTrigger(null);
  }, [trigger, onJobQueryChange]);

  const handleInputChange = useCallback((
    value: string,
    onChange: (value: string) => void
  ) => {
    onChange(value);

    // Extract and update mentioned users from the text
    extractAndUpdateMentionedUsers(value);

    pruneTaggedJobs(value, taggedJobs);

    if (!textFieldRef.current) return;

    const cursorPosition = textFieldRef.current.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPosition);

    const isValidStart = (index: number) => {
      const charBefore = index > 0 ? value[index - 1] : ' ';
      return charBefore === ' ' || charBefore === '\n' || index === 0;
    };

    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const lastHashIndex = jobTagging ? textBeforeCursor.lastIndexOf('#') : -1;

    // `#` job context: whichever trigger is nearest the caret wins.
    if (lastHashIndex !== -1 && lastHashIndex > lastAtIndex && isValidStart(lastHashIndex)) {
      const query = textBeforeCursor.substring(lastHashIndex + 1);

      // The caret sitting after a finished tag is not a search.
      const onExistingTag = findJobTagRanges(value, taggedJobs).some((range) => range.start === lastHashIndex);

      // "#1 priority": once a query that has found nothing grows a space, it is prose. The search
      // matches the whole query, so a longer one cannot find what a shorter one did not.
      const foundNothing = query.includes(' ') && !jobsLoading && jobs.length === 0;

      if (
        !onExistingTag &&
        !foundNothing &&
        dismissedJobStart !== lastHashIndex &&
        !query.startsWith(' ') &&
        !query.includes('\n') &&
        query.length <= MAX_JOB_QUERY_LENGTH
      ) {
        setMentionStart(lastHashIndex);
        setTrigger('#');
        setAnchorEl(textFieldRef.current);
        onJobQueryChange?.(query);
        return;
      }
    }

    if (trigger === '#') onJobQueryChange?.(null);

    if (dismissedJobStart !== null && dismissedJobStart !== lastHashIndex) {
      setDismissedJobStart(null);
    }

    // check if we're in a mention context
    if (lastAtIndex !== -1) {
      const isValidMentionStart = isValidStart(lastAtIndex);

      if (isValidMentionStart) {
        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

        // only show suggestions if there's no space or newline after @
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          setMentionStart(lastAtIndex);
          setTrigger('@');

          // filter users
          const filtered = users.filter(user => {
            const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
            return fullName.includes(textAfterAt.toLowerCase());
          });

          setSuggestions(filtered);
          setSelectedIndex(0);

          if (filtered.length > 0) {
            setAnchorEl(textFieldRef.current);
          } else {
            setAnchorEl(null);
          }
          return;
        }
      }
    }

    setAnchorEl(null);
    setMentionStart(null);
    setTrigger(null);
  }, [users, extractAndUpdateMentionedUsers, pruneTaggedJobs, taggedJobs, jobTagging, jobs, jobsLoading, dismissedJobStart, trigger, onJobQueryChange]);

  // Replaces the typed trigger and query with `inserted`, and puts the caret after it.
  const replaceQuery = useCallback((
    inserted: string,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    if (mentionStart === null || !textFieldRef.current) return null;

    const cursorPosition = textFieldRef.current.selectionStart || 0;
    const beforeMention = currentValue.substring(0, mentionStart);
    const afterMention = currentValue.substring(cursorPosition);

    const newValue = `${beforeMention + inserted  } ${  afterMention}`;

    onChange(newValue);

    setTimeout(() => {
      if (textFieldRef.current) {
        const newPosition = beforeMention.length + inserted.length + 1;
        textFieldRef.current.setSelectionRange(newPosition, newPosition);
        textFieldRef.current.focus();
      }
    }, 0);

    return newValue;
  }, [mentionStart]);

  const insertMention = useCallback((
    user: MentionUser,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    // format: @firstname lastname
    const newValue = replaceQuery(`@${user.first_name} ${user.last_name}`, currentValue, onChange);

    if (newValue === null) return;

    extractAndUpdateMentionedUsers(newValue);

    setAnchorEl(null);
    setMentionStart(null);
    setTrigger(null);
    setSuggestions([]);
  }, [replaceQuery, extractAndUpdateMentionedUsers]);

  const insertJobTag = useCallback((
    job: MentionJob,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    const newValue = replaceQuery(formatJobTag(job.title), currentValue, onChange);

    if (newValue === null) return;

    const next = taggedJobs.some((tagged) => tagged.id === job.id) ? taggedJobs : [...taggedJobs, job];
    onTaggedJobsChange?.(keepTaggedJobsInText(newValue, next));

    onJobQueryChange?.(null);
    setAnchorEl(null);
    setMentionStart(null);
    setTrigger(null);
  }, [replaceQuery, taggedJobs, onTaggedJobsChange, onJobQueryChange]);

  const handleKeyDown = useCallback((
    e: React.KeyboardEvent,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    if (!anchorEl) return;

    const count = trigger === '#' ? jobs.length : suggestions.length;

    if (e.key === 'Escape') {
      e.preventDefault();
      if (trigger === '#') setDismissedJobStart(mentionStart);
      closeSuggestions();
      return;
    }

    if (count === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % count);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + count) % count);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (trigger === '#') {
        insertJobTag(jobs[Math.min(selectedIndex, count - 1)], currentValue, onChange);
      } else {
        insertMention(suggestions[selectedIndex], currentValue, onChange);
      }
    }
  }, [anchorEl, trigger, jobs, suggestions, selectedIndex, mentionStart, closeSuggestions, insertJobTag, insertMention]);

  const renderUserSuggestions = (onPick: (user: MentionUser) => void) => (
    suggestions.length > 0 ? (
      suggestions.map((user, index) => (
        <ListItem key={user.id} disablePadding>
          <ListItemButton
            selected={index === selectedIndex}
            onClick={() => onPick(user)}
          >
            <ListItemText
              primary={`${user.first_name} ${user.last_name}`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItemButton>
        </ListItem>
      ))
    ) : (
      <ListItem>
        <ListItemText
          primary="No users found"
          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
        />
      </ListItem>
    )
  );

  const renderJobSuggestions = (onPick: (job: MentionJob) => void) => {
    if (jobs.length === 0) {
      return (
        <ListItem>
          <ListItemText
            primary={jobsLoading ? 'Searching jobs…' : 'No jobs found'}
            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
          />
        </ListItem>
      );
    }

    return jobs.map((job, index) => (
      <ListItem key={job.id} disablePadding>
        <ListItemButton
          selected={index === selectedIndex}
          // Keep focus in the text field, so the caret position the insert relies on survives.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(job)}
        >
          <ListItemText
            primary={job.title}
            secondary={job.subtitle}
            primaryTypographyProps={{ variant: 'body2' }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItemButton>
      </ListItem>
    ));
  };

  return (
    <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          // JT1-1561: `other` is spread FIRST so a caller-supplied onKeyDown can no longer replace
          // the mention handler below - that silently killed Enter-to-select in the suggestion list.
          // The caller's handler still runs, but only after the mention list has had its say.
          const { onKeyDown: callerOnKeyDown, ...rest } = other as TextFieldProps;

          const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
            handleKeyDown(e, field.value || '', field.onChange);

            if (e.defaultPrevented) return;

            callerOnKeyDown?.(e as React.KeyboardEvent<HTMLDivElement>);
          };

          return (
            <>
              <TextField
                {...field}
                fullWidth
                inputRef={textFieldRef}
                {...rest}
                onChange={(event) => {
                  handleInputChange(event.target.value, field.onChange);
                }}
                onKeyDown={onKeyDown}
                error={!!error}
                helperText={error ? error?.message : helperText}
              />

              <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
              >
                <Paper elevation={8} sx={{ maxHeight: 200, overflow: 'auto', minWidth: 250 }}>
                  <List dense>
                    {trigger === '#'
                      ? renderJobSuggestions((job) => insertJobTag(job, field.value || '', field.onChange))
                      : renderUserSuggestions((user) => insertMention(user, field.value || '', field.onChange))}
                  </List>
                </Paper>
              </Popper>
            </>
          );
        }}
      />
  );
}
